import { Stack, Typography, Chip, Box, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ConnectorStatus } from '../../../common/enums/connectorStatus';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import WarningIcon from '@mui/icons-material/Warning';

interface PauseProps {
  status: ConnectorStatus.Suspended_EV | ConnectorStatus.Suspended_EVSE;
  connectorId?: number;
}

export const Pause: React.FC<PauseProps> = ({ status, connectorId }) => {
  const { t } = useTranslation();

  const getPauseInfo = () => {
    switch (status) {
      case ConnectorStatus.Suspended_EV:
        return {
          title: t('pause.suspendedByEV'),
          description: t('pause.suspendedByEVDescription'),
          color: 'green' as const,
          icon: <PauseCircleIcon sx={{ fontSize: '24px' }} />,
          actionMessage: t('pause.evActionMessage'),
        };
      case ConnectorStatus.Suspended_EVSE:
        return {
          title: t('pause.suspendedByStation'),
          description: t('pause.suspendedByStationDescription'),
          color: 'orange' as const,
          icon: <WarningIcon sx={{ fontSize: '24px' }} />,
          actionMessage: t('pause.waitingMessage'),
        };
      default:
        return {
          title: t('pause.suspended'),
          description: t('pause.suspendedDescription'),
          color: 'orange' as const,
          icon: <PauseCircleIcon sx={{ fontSize: '24px' }} />,
          actionMessage: t('pause.waitingMessage'),
        };
    }
  };

  const pauseInfo = getPauseInfo();

  return (
    <Stack
      spacing={3}
      sx={(theme: Theme) => {
        const accentColor =
          pauseInfo.color === 'green'
            ? theme.palette.green.main
            : theme.palette.orange.main;

        return {
          width: '100%',
          maxWidth: '400px',
          margin: '0 auto',
          padding: '24px',
          backgroundColor: theme.palette.gray[200],
          borderRadius: '10px',
          boxShadow: '0 1px 4px rgba(17, 17, 19, .3)',
          border: '2px solid',
          borderColor: accentColor,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            backgroundColor: accentColor,
            borderRadius: '2px 0 0 2px',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            backgroundColor: accentColor,
            borderRadius: '0 2px 2px 0',
          },
        };
      }}
    >
      {/* Header with Icon and Title */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={(theme: Theme) => {
            const accentColor =
              pauseInfo.color === 'green'
                ? theme.palette.green.main
                : theme.palette.orange.main;

            return {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              backgroundColor: accentColor,
              borderRadius: '12px',
              color: 'white',
            };
          }}
        >
          {pauseInfo.icon}
        </Box>

        <Stack spacing={0.5}>
          <Typography
            variant="h6"
            color="common.white"
            sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}
          >
            {pauseInfo.title}
          </Typography>
          {connectorId && (
            <Typography
              variant="body2"
              color="common.white"
              sx={{ fontSize: '0.9rem', opacity: 0.8 }}
            >
              {t('pause.connector')} {connectorId}
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* Status Chip */}
      <Chip
        label={pauseInfo.title}
        sx={(theme: Theme) => {
          const accentColor =
            pauseInfo.color === 'green'
              ? theme.palette.green.main
              : theme.palette.orange.main;

          return {
            backgroundColor: accentColor,
            color: theme.palette.getContrastText(accentColor),
            fontWeight: 'bold',
            fontSize: '1rem',
            height: '40px',
            alignSelf: 'center',
            '& .MuiChip-label': {
              color: theme.palette.getContrastText(accentColor),
              fontWeight: 'bold',
            },
          };
        }}
      />

      {/* Description */}
      <Typography
        variant="body1"
        color="common.white"
        textAlign="left"
        sx={{
          lineHeight: 1.6,
          padding: '0 16px',
          fontSize: '0.95rem',
          opacity: 0.9,
        }}
      >
        {pauseInfo.description}
      </Typography>

      {/* Additional Info */}
      <Box
        sx={(theme: Theme) => {
          const accentColor =
            pauseInfo.color === 'green'
              ? theme.palette.green.main
              : theme.palette.orange.main;

          return {
            backgroundColor: accentColor,
            padding: '16px',
            borderRadius: '12px',
            textAlign: 'center',
          };
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'medium',
            fontSize: '0.9rem',
            color: (theme) =>
              pauseInfo.color === 'green'
                ? theme.palette.common.white
                : theme.palette.common.black,
          }}
        >
          {pauseInfo.actionMessage}
        </Typography>
      </Box>
    </Stack>
  );
};
